import { FC, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { useLocalize } from '@treams/treams-hooks';
import { CareerPartialFragment, JobTitle } from '@treams/treams-network';
import { StepTimeline, TimelineItem } from '@treams/treams-ui';

import { useGrowthPathContext } from '../contexts/GrowthPathContext';

type Props = {
	career: CareerPartialFragment;
	onClick: (selectedJobTitle: Partial<JobTitle>) => void;
	linkedJobTitle?: Partial<JobTitle>;
};

const JobTitleButtonTimeline: FC<Props> = ({ career, onClick, linkedJobTitle }) => {
	const { localize } = useLocalize();
	const history = useHistory();

	const { userID, jobTitleID, setJobTitleID } = useGrowthPathContext();

	const highlight = useRef<null | HTMLDivElement>(null);
	const itemRefs = useRef<Array<null | HTMLDivElement>>([]);

	const [activeIndex, setActiveIndex] = useState(0);

	useEffect(() => {
		if (highlight.current) {
			const MARGIN_BETWEEN_BUTTONS = 2;

			const buttonHeightMiddleArray = itemRefs.current.map((item) =>
				item ? item.clientHeight / 2 : 0,
			);
			let height = 0;

			if (activeIndex === 1) {
				height = buttonHeightMiddleArray[0] + buttonHeightMiddleArray[1] + MARGIN_BETWEEN_BUTTONS;
			} else if (activeIndex > 1) {
				const begin = buttonHeightMiddleArray[0] + buttonHeightMiddleArray[1] + 2;

				const addUpNumbers: number[] = [];

				for (let i = 2; i < activeIndex + 1; i++) {
					addUpNumbers.push(
						buttonHeightMiddleArray[i] + buttonHeightMiddleArray[i - 1] + MARGIN_BETWEEN_BUTTONS,
					);
				}

				height = addUpNumbers.reduce((sum, num) => sum + num, begin);
			}

			highlight.current.style.transform = `translateY(${height}px)`;
		}
	}, [activeIndex]);

	useEffect(() => {
		const jobTitle =
			linkedJobTitle ??
			career.jobTitles.find((jobTitle) => jobTitle.id === jobTitleID) ??
			career.jobTitles[0];
		const index = career.jobTitles.findIndex((j) => j.id === jobTitle.id);

		updateSelectedJobTitle(index, jobTitle, false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [linkedJobTitle]);

	const updateSelectedJobTitle = (index: number, jobTitle: Partial<JobTitle>, linked: boolean) => {
		onClick(jobTitle);
		setActiveIndex(index);

		if (linkedJobTitle === undefined) {
			setJobTitleID(jobTitle.id!);
		}

		if (linked && userID) {
			history.push(`/users/${userID}/growth-path/career/${career.id}/job-title/${jobTitle.id}`);

			return;
		}

		if (linked && !userID) {
			history.push(`/growth-path/career/${career.id}/job-title/${jobTitle.id}`);
		}
	};

	const items: TimelineItem[] = career.jobTitles.map((jobTitle) => {
		return {
			id: jobTitle.id,
			title: localize(jobTitle.title).text,
		};
	});

	const onJobTitleClick = (index: number) => {
		try {
			const jobTitle = career.jobTitles[index];

			updateSelectedJobTitle(index, jobTitle, true);
		} catch {
			throw Error('JobTitle not found for index');
		}
	};

	return <StepTimeline items={items} activeIndex={activeIndex} onItemClick={onJobTitleClick} />;
};

export default JobTitleButtonTimeline;
